import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { helpKey } from '@/lib/action-modules/help'
import { ScheduleDaysDefinitionEditor } from './ScheduleDaysDefinitionEditor'
import { ScheduleWeeksDefinitionEditor } from './ScheduleWeeksDefinitionEditor'
import { ScheduleMonthsDefinitionEditor } from './ScheduleMonthsDefinitionEditor'
import { ScheduleQuarterDefinitionEditor } from './ScheduleQuarterDefinitionEditor'
import { ScheduleTimeDefinitionEditor } from './ScheduleTimeDefinitionEditor'
import { ScheduleWeekMode, ScheduleMonthMode, ScheduleQuarterMode } from '@/api/model'
import { ScheduleDefinitionType } from '@/lib/action-modules/registry/scheduling/types'
import type { ScheduleDefinitionBase, ScheduleDay, ScheduleWeek, ScheduleMonth, ScheduleQuarter, ScheduleTimePeriod, ScheduleTimePeriodsDefinition, ScheduleWeekDefinition, ScheduleMonthDefinition, ScheduleQuarterDefinition, ScheduleDaysDefinition } from '@/lib/action-modules/registry/scheduling/types'

interface Props {
  scheduleDefinition: ScheduleDefinitionBase
  onUpdateScheduleDefinition: (scheduleDefinition: ScheduleDefinitionBase) => void
  disabled: boolean
}

// Reverse of the composer below: split an incoming (fetched) discriminated definition back into
// the per-granularity slices the editor holds in state, so editing an existing schedule starts
// populated instead of blank. The nesting is quarter → month → week → days; each slice keeps only
// its "own" fields (the composer re-attaches the children).
function decomposeScheduleDefinition(def: ScheduleDefinitionBase | null | undefined) {
  const type = def?.discriminator ?? ScheduleDefinitionType.Day
  const base: ScheduleDefinitionBase = {
    id: def?.id ?? 0,
    discriminator: type,
    combineConsecutiveDays: def?.combineConsecutiveDays ?? false,
    separateByWeek: def?.separateByWeek ?? false,
    separateByMonth: def?.separateByMonth ?? false,
    startDate: def?.startDate ?? null,
    endDate: def?.endDate ?? null,
  }

  let days: ScheduleDay[] = []
  let week: ScheduleWeek = { mode: ScheduleWeekMode.None, value: 0 }
  let month: ScheduleMonth = { mode: ScheduleMonthMode.None, value: 0, weekOffsetInsideMonth: 0 }
  let quarter: ScheduleQuarter = { mode: ScheduleQuarterMode.None, value: 0, monthOffsetInsideQuarter: 0 }
  let timePeriods: ScheduleTimePeriod[] = []

  const applyWeek = (w?: ScheduleWeek) => {
    if (!w) return
    week = { mode: w.mode, value: w.value }
    days = w.days ?? []
  }
  const applyMonth = (m?: ScheduleMonth) => {
    if (!m) return
    month = { mode: m.mode, value: m.value, weekOffsetInsideMonth: m.weekOffsetInsideMonth }
    applyWeek(m.week)
  }

  switch (type) {
    case ScheduleDefinitionType.Time:
      timePeriods = (def as ScheduleTimePeriodsDefinition | null)?.timePeriods ?? []
      break
    case ScheduleDefinitionType.Week:
      applyWeek((def as ScheduleWeekDefinition | null)?.week)
      break
    case ScheduleDefinitionType.Month:
      applyMonth((def as ScheduleMonthDefinition | null)?.month)
      break
    case ScheduleDefinitionType.Quarter: {
      const q = (def as ScheduleQuarterDefinition | null)?.quarter
      if (q) {
        quarter = { mode: q.mode, value: q.value, monthOffsetInsideQuarter: q.monthOffsetInsideQuarter }
        applyMonth(q.month)
      }
      break
    }
    case ScheduleDefinitionType.Day:
    default:
      days = (def as ScheduleDaysDefinition | null)?.days ?? []
      break
  }

  return { base, type, days, week, month, quarter, timePeriods }
}

// Ported faithfully from the old portal: the composer keeps the per-granularity models in its
// own state and assembles the discriminated `ScheduleDefinition` for the parent on every change.
export default function ScheduleDefinitionEditor(props: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const sd = (key: string) => t(`schedulingStrategy.scheduleDefinition.${key}`)
  const { onUpdateScheduleDefinition } = props
  // Hydrate the slices once from the fetched definition (see decomposeScheduleDefinition).
  const [initial] = useState(() => decomposeScheduleDefinition(props.scheduleDefinition))
  const [schedulingType, setSchedulingType] = useState<ScheduleDefinitionType>(initial.type)
  const [daySchedule, setDaySchedule] = useState<ScheduleDay[]>(initial.days)
  const [weekSchedule, setWeekSchedule] = useState<ScheduleWeek>(initial.week)
  const [monthSchedule, setMonthSchedule] = useState<ScheduleMonth>(initial.month)
  const [quarterSchedule, setQuarterSchedule] = useState<ScheduleQuarter>(initial.quarter)
  const [timeModel, setTimeModel] = useState<ScheduleTimePeriod[]>(initial.timePeriods)
  const [base, setBase] = useState<ScheduleDefinitionBase>(initial.base)

  useEffect(() => {
    const week: ScheduleWeek = { ...weekSchedule, days: daySchedule }
    const month: ScheduleMonth = { ...monthSchedule, week }
    const quarter: ScheduleQuarter = { ...quarterSchedule, month }

    let result: ScheduleDefinitionBase
    switch (schedulingType) {
      case ScheduleDefinitionType.Time:
        result = { ...base, discriminator: schedulingType, timePeriods: timeModel } as ScheduleTimePeriodsDefinition
        break
      case ScheduleDefinitionType.Week:
        result = { ...base, discriminator: schedulingType, week } as ScheduleWeekDefinition
        break
      case ScheduleDefinitionType.Month:
        result = { ...base, discriminator: schedulingType, month } as ScheduleMonthDefinition
        break
      case ScheduleDefinitionType.Quarter:
        result = { ...base, discriminator: schedulingType, quarter } as ScheduleQuarterDefinition
        break
      case ScheduleDefinitionType.Day:
      default:
        result = { ...base, discriminator: schedulingType, days: daySchedule } as ScheduleDaysDefinition
        break
    }
    onUpdateScheduleDefinition(result)
    // `onUpdateScheduleDefinition` deliberately excluded: the parent passes a fresh callback
    // each render; including it would re-fire the effect in a loop (oxlint doesn't enforce deps).
  }, [schedulingType, daySchedule, weekSchedule, monthSchedule, quarterSchedule, timeModel, base])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex max-w-md flex-col gap-2">
        <HelpLabel helpKey={helpKey('schedulingBaseMode')}>{sd('baseMode')}</HelpLabel>
        <Select value={schedulingType} onValueChange={v => setSchedulingType(v as ScheduleDefinitionType)} disabled={props.disabled}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ScheduleDefinitionType.Day}>{sd('days.name')}</SelectItem>
            <SelectItem value={ScheduleDefinitionType.Week}>{sd('weeks.name')}</SelectItem>
            <SelectItem value={ScheduleDefinitionType.Month}>{sd('months.name')}</SelectItem>
            <SelectItem value={ScheduleDefinitionType.Quarter}>{sd('quarter.name')}</SelectItem>
            <SelectItem value={ScheduleDefinitionType.Time}>{sd('time.name')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <HelpLabel helpKey={helpKey('schedulingStartDate')}>{sd('start')}</HelpLabel>
          <Input
            type="date"
            value={base.startDate ?? ''}
            onChange={e => setBase({ ...base, startDate: e.target.value || null })}
          />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <HelpLabel helpKey={helpKey('schedulingEndDate')}>{sd('end')}</HelpLabel>
          <Input
            type="date"
            value={base.endDate ?? ''}
            onChange={e => setBase({ ...base, endDate: e.target.value || null })}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <HelpLabel
          helpKey={helpKey('schedulingCombineConsecutiveDays')}
          labelClassName="flex items-center gap-2 font-normal"
        >
          <Checkbox
            checked={base.combineConsecutiveDays}
            onCheckedChange={c => setBase({ ...base, combineConsecutiveDays: c === true })}
          />
          {sd('combineConsecutiveDays')}
        </HelpLabel>
        <HelpLabel helpKey={helpKey('schedulingSeparateByWeek')} labelClassName="flex items-center gap-2 font-normal">
          <Checkbox checked={base.separateByWeek} onCheckedChange={c => setBase({ ...base, separateByWeek: c === true })} />
          {sd('separateByWeek')}
        </HelpLabel>
        <HelpLabel helpKey={helpKey('schedulingSeparateByMonth')} labelClassName="flex items-center gap-2 font-normal">
          <Checkbox
            checked={base.separateByMonth}
            onCheckedChange={c => setBase({ ...base, separateByMonth: c === true })}
          />
          {sd('separateByMonth')}
        </HelpLabel>
      </div>

      {schedulingType === ScheduleDefinitionType.Day && (
        <ScheduleDaysDefinitionEditor dayModel={daySchedule} onUpdateDayModel={setDaySchedule} />
      )}
      {schedulingType === ScheduleDefinitionType.Week && (
        <ScheduleWeeksDefinitionEditor
          dayModel={daySchedule}
          onUpdateDayModel={setDaySchedule}
          weekModel={weekSchedule}
          onUpdateWeekModel={setWeekSchedule}
        />
      )}
      {schedulingType === ScheduleDefinitionType.Month && (
        <ScheduleMonthsDefinitionEditor
          dayModel={daySchedule}
          onUpdateDayModel={setDaySchedule}
          weekModel={weekSchedule}
          onUpdateWeekModel={setWeekSchedule}
          monthModel={monthSchedule}
          onUpdateMonthModel={setMonthSchedule}
        />
      )}
      {schedulingType === ScheduleDefinitionType.Quarter && (
        <ScheduleQuarterDefinitionEditor
          dayModel={daySchedule}
          onUpdateDayModel={setDaySchedule}
          weekModel={weekSchedule}
          onUpdateWeekModel={setWeekSchedule}
          monthModel={monthSchedule}
          onUpdateMonthModel={setMonthSchedule}
          quarterModel={quarterSchedule}
          onUpdateQuarterModel={setQuarterSchedule}
        />
      )}
      {schedulingType === ScheduleDefinitionType.Time && (
        <ScheduleTimeDefinitionEditor timeModel={timeModel} onUpdateTimeModel={setTimeModel} />
      )}
    </div>
  )
}
